import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Link,
    List,
    Loader,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { OrganizationType } from '@common/enums';
import type { AgbCode } from '@common/interfaces/agbCode';
import type {
    OrganizationLogFindAllForOrganizationResult,
    OrganizationLogGetItem,
} from '@common/interfaces/organizationLog';
import type { Stringified } from '@common/utils/stringify';
import { OrganizationLogModifierField } from '@common/enums/organizationLogModifierField';
import { parseStringified } from '@common/utils/stringify';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';

interface IOrganizationLogsTableProps {
    organizationId: string;
}

export function OrganizationLogsTable({ organizationId }: Readonly<IOrganizationLogsTableProps>) {
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();
    const [isLoading, setIsLoading] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [organizationLogs, setOrganizationLogs] = useState<OrganizationLogFindAllForOrganizationResult>({
        items: [],
        total: 0,
    });

    useEffect(() => {
        const getOrganizationLogs = async () => {
            setIsLoading(true);
            const logs = await fetchOrganizationLogs();

            setOrganizationLogs(logs);
            setIsLoading(false);
        };

        getOrganizationLogs();
    }, [organizationId, itemsPerPage, currentPage]);

    return (
        <>
            {isLoading && (
                <Box
                    display="flex"
                    justifyContent="center"
                    mb={4}
                >
                    <Loader size="medium" />
                </Box>
            )}
            {!isLoading && (
                <Box mb={8}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{translate.organizationLogs.modificationDate}</TableHeader>
                                    <TableHeader>{translate.organizationLogs.effectiveDate}</TableHeader>
                                    <TableHeader>{translate.organizationLogs.modifiedBy}</TableHeader>
                                    <TableHeader>{translate.organizationLogs.modificationType}</TableHeader>
                                    <TableHeader>{translate.organizationLogs.oldValue}</TableHeader>
                                    <TableHeader>{translate.organizationLogs.newValue}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {organizationLogs.items.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell ellipsis>
                                            {DateTime.from(log.created_at).format('DD-MM-YYYY')}
                                        </TableCell>
                                        <TableCell ellipsis>
                                            {log.newValueStartDate
                                                ? DateTime.from(log.newValueStartDate).format('DD-MM-YYYY')
                                                : ''}
                                        </TableCell>
                                        <TableCell ellipsis>{renderPractitioner(log)}</TableCell>
                                        <TableCell>
                                            {translate.organizationLogs.modifiedField[log.modifiedField]}
                                        </TableCell>
                                        <TableCell>{renderValue(log.modifiedField, log.oldValue)}</TableCell>
                                        <TableCell>{renderValue(log.modifiedField, log.newValue)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination
                        onChange={(params) => {
                            setItemsPerPage(params.pageSize);
                            setCurrentPage(params.page);
                        }}
                        page={currentPage}
                        pageSize={itemsPerPage}
                        totalItems={organizationLogs.total}
                    />
                </Box>
            )}
        </>
    );

    function renderPractitioner(logItem: OrganizationLogGetItem) {
        return (
            <>
                {logItem.modifierPractitionerId ? (
                    <Link
                        to={`/practitioners/${logItem.modifierPractitionerId}/profile`}
                        variant="link"
                    >
                        {logItem.modifierPractitionerDisplayName}
                    </Link>
                ) : (
                    logItem.modifierPractitionerDisplayName
                )}
            </>
        );
    }

    function renderValue(modifiedField: keyof typeof OrganizationLogModifierField, value: string | null) {
        if (!value) {
            return '';
        }

        switch (modifiedField) {
            case OrganizationLogModifierField.organizationType:
                return translate.organizationTypeEnum[value as OrganizationType];

            case OrganizationLogModifierField.agbCodes: {
                const agbCodes = parseStringified(value as Stringified<AgbCode[]>);

                return (
                    <>
                        <List size="small">
                            {agbCodes.map((agbCode, index) => (
                                <List.Item key={index}>{formatAgbCode(agbCode)}</List.Item>
                            ))}
                        </List>
                    </>
                );
            }

            default:
                return value;
        }
    }

    function formatAgbCode({ agbCode, endDate, startDate }: AgbCode) {
        return `${agbCode}: ${DateTime.from(startDate).format('DD-MM-YYYY')} - ${
            endDate ? `${DateTime.from(endDate).format('DD-MM-YYYY')}` : `${translate.common.now.toLowerCase()}`
        }`;
    }

    async function fetchOrganizationLogs(): Promise<OrganizationLogFindAllForOrganizationResult> {
        const response = await abortableFetch.getJson(
            `/api/organizations/${organizationId}/organizationLogs?limit=${itemsPerPage}&offset=${
                (currentPage - 1) * itemsPerPage
            }`
        );

        return response.json;
    }
}
